/**
 * Network tier classification.
 *
 * The gateway's `network_env` string is a cosmetic display label only (the
 * gateway keeps it for back-compat); it can read "mainnet" on a deployment whose
 * chains are all testnet. The authoritative signals are which tiers the
 * deployment actually serves (`serves_mainnet` / `serves_testnet`) plus each
 * wallet's immutable `chain` key. This module ports the mobile app's classifier
 * so the web client never labels testnet balances as mainnet.
 *
 * A wallet showing real (mainnet) value when it is testnet is a safety problem,
 * so every ambiguous case fails safe to testnet.
 */

import type { BalanceLine, WalletSummary } from './types';

/** The deployment signals needed to classify a tier, read from /v1/config. */
export interface NetworkInfo {
  /** Whether the deployment actually serves mainnet. Null until config loads. */
  servesMainnet: boolean | null;
  /** Whether the deployment actually serves testnet. Null until config loads. */
  servesTestnet: boolean | null;
  /**
   * The tier the user picked in Developer Mode. Developer Mode ON means testnet,
   * OFF means mainnet. Null falls back to the deployment default (testnet). The
   * selection is clamped to a served tier by effectiveEnvironment, so a choice
   * the deployment cannot serve never takes effect.
   */
  selectedEnv: 'testnet' | 'mainnet' | null;
  /** The real Bitcoin network, for example "regtest", "testnet", or "mainnet". */
  bitcoinNetwork: string | null;
  /**
   * Whether the deployment serves Bitcoin mainnet, reported separately from
   * servesMainnet. Optional because most classification does not need it; when
   * absent it is treated as not served, which fails safe by hiding a Bitcoin
   * mainnet create. Only the create flow reads it, via bitcoinCreatable.
   */
  bitcoinMainnetServed?: boolean | null;
  /** The raw network_env label, used only as the tier hint for bare solana/sui. */
  environment: string;
}

/** Chain keys that are unambiguously testnet. */
const TESTNET_CHAINS = new Set<string>([
  'sepolia',
  'bsc-testnet',
  'base-sepolia',
  'hyperevm-testnet',
  'tron-nile',
  'solana-devnet',
  'sui-testnet',
]);

/** Chain keys that are unambiguously mainnet. */
const MAINNET_CHAINS = new Set<string>([
  'ethereum',
  'bsc',
  'base',
  'hyperevm',
  'tron',
  'bitcoin-mainnet',
  'bitcoin-taproot-mainnet',
]);

/**
 * The effective environment of the deployment, clamping the user's Developer
 * Mode selection to a tier the deployment actually serves. Developer Mode ON
 * selects testnet, OFF selects mainnet; a selection the deployment cannot serve
 * is ignored in favor of a served tier. A new user's selection defaults to
 * mainnet (set at boot from the persisted choice in the store), but a genuinely
 * absent selection here, or a tier the deployment does not serve, still fails
 * safe to testnet so nothing is labeled mainnet before the truth is known.
 *
 * @param info Deployment signals from the config plus the user's selection.
 * @returns The effective environment.
 */
export function effectiveEnvironment(info: NetworkInfo): 'testnet' | 'mainnet' {
  const selected = info.selectedEnv ?? 'testnet';
  const servesT = info.servesTestnet ?? true;
  const servesM = info.servesMainnet ?? false;
  // Honor the selection when the deployment serves that tier.
  if (selected === 'testnet' && servesT) return 'testnet';
  if (selected === 'mainnet' && servesM) return 'mainnet';
  // The selection is not served; fall back to whichever tier is, testnet first.
  if (servesT) return 'testnet';
  if (servesM) return 'mainnet';
  return 'testnet';
}

/**
 * The concrete Tron chain key for the deployment's active tier: "tron" on
 * mainnet and "tron-nile" on testnet. Tron shares the secp256k1 curve with the
 * unified EVM account, so a wallet create that leaves the chain null resolves to
 * EVM at the gateway. The gateway registry has no tier-agnostic Tron sentinel,
 * only these two concrete keys, so the tier-correct key must be chosen on the
 * client and sent explicitly. The chain is immutable on the wallet record once
 * created, so this reads the effective environment (which clamps the Developer
 * Mode selection to a served tier and fails safe to testnet) rather than the raw
 * selection, keeping a wallet from being stranded on the wrong tier.
 *
 * @param info Deployment signals plus the user's Developer Mode selection.
 * @returns "tron" on mainnet, "tron-nile" on testnet.
 */
export function tronChainForTier(info: NetworkInfo): 'tron' | 'tron-nile' {
  return effectiveEnvironment(info) === 'mainnet' ? 'tron' : 'tron-nile';
}

/**
 * Whether a Bitcoin wallet can be created on the active tier. Bitcoin is always
 * available on testnet; on mainnet it requires the deployment to actually serve
 * Bitcoin mainnet, which is reported separately from serves_mainnet because the
 * Bitcoin adapter needs its own node and can lag the other mainnet chains. The
 * gateway rejects a create for an unserved tier fail-closed, so a caller hides
 * the Bitcoin option when this returns false to keep a new user (who now
 * defaults to mainnet) off that dead-end. An unknown flag counts as not served.
 *
 * @param info Deployment signals plus the user's Developer Mode selection.
 * @returns True when a Bitcoin create is servable on the active tier.
 */
export function bitcoinCreatable(info: NetworkInfo): boolean {
  if (effectiveEnvironment(info) === 'testnet') return true;
  return info.bitcoinMainnetServed === true;
}

/**
 * Whether the swap ENTRY POINTS are surfaced on the active tier — the bottom-nav
 * tab and the home action row. Now true on both tiers.
 *
 * THIS IS NOT THE EXECUTION GATE. What decides whether a swap can be signed is
 * swapExecutability in lib/swapExecute, and it no longer refuses mainnet either:
 * the tier gate there was removed at the operator's instruction, leaving the
 * executable-chain map as the only thing between a quote and a signature.
 *
 * So this function being true is not, on its own, a statement that mainnet swaps
 * are safe. The swap path still does not decode calldata, enforce a minReceived
 * floor or a slippage bound, or check the router against an allowlist. Those
 * were deferred on the understanding that testnet confinement stood in for them;
 * that confinement is gone and they are still unimplemented. The note at the top
 * of lib/swapExecute is the authority on that debt — keep this comment pointing
 * at it rather than restating a posture that has already changed once.
 *
 * @param info Deployment signals plus the user's Developer Mode selection.
 * @returns True — swap is offered on both tiers.
 */
export function swapEnabled(_info: NetworkInfo): boolean {
  return true;
}

/**
 * Whether the Earn (lending) surface is offered. Same gate as swap for the
 * same reason: the lending market only exists on testnet today, so the tab
 * appears in testnet mode and is absent on mainnet. Widening the gate once a
 * mainnet market ships is a one-line change here.
 *
 * @param info Deployment signals plus the user's Developer Mode selection.
 * @returns True when Earn should be shown on the active tier.
 */
export function lendingEnabled(info: NetworkInfo): boolean {
  return effectiveEnvironment(info) === 'testnet';
}

/**
 * Whether one wallet's balances are on a testnet. Classifies by the wallet's
 * chain key first (decisive for tier-bound keys), treats bare Bitcoin by the
 * deployment's Bitcoin network, and lets tier-agnostic keys and null chains
 * follow the effective environment, falling back to the curve family for a null
 * chain. Fails safe to testnet.
 *
 * @param wallet The wallet to classify.
 * @param info Deployment signals from the config.
 * @returns True when the wallet is on a testnet.
 */
export function walletIsTestnet(
  wallet: Pick<WalletSummary, 'chain' | 'curve'>,
  info: NetworkInfo,
): boolean {
  const chain = wallet.chain?.toLowerCase() ?? null;
  const curve = wallet.curve?.toLowerCase() ?? null;
  const env = effectiveEnvironment(info);

  // The permanently-mainnet and explicit-testnet keys are unambiguous, so they
  // decide the tier before any other signal.
  if (chain) {
    if (TESTNET_CHAINS.has(chain)) return true;
    if (MAINNET_CHAINS.has(chain)) return false;
  }

  // A Bitcoin wallet takes its tier from the deployment's Bitcoin network. It is
  // recognized by any bitcoin chain key or, for a wallet created before its
  // chain key was stamped, by the Taproot curve. This keeps a regtest or testnet
  // Bitcoin wallet out of mainnet mode even when its stored chain key is missing
  // or an unrecognized variant.
  const isBitcoin =
    (chain != null && chain.startsWith('bitcoin')) || curve === 'schnorr-taproot-secp256k1';
  if (isBitcoin) {
    return (info.bitcoinNetwork ?? '').toLowerCase() !== 'mainnet';
  }

  // Tier-agnostic keys (evm, solana, sui), an unrecognized key, or a wallet with
  // no chain key yet follow the deployment's effective environment.
  return env === 'testnet';
}

/**
 * The tier a bare chain key names when the key is tier-bound, independent of any
 * deployment signal. A tier-bound key is one of the explicit testnet or mainnet
 * chains; everything else (a tier-agnostic family key like "evm" or "solana", a
 * null key, or an unrecognized one) is not tier-bound on its own and returns
 * null so the caller defers to a wallet-level or effective-environment reading.
 *
 * This is the key-only counterpart to lineIsTestnet, which classifies a balance
 * line by its per-line network. Both consult the same TESTNET_CHAINS and
 * MAINNET_CHAINS sets so a chain key means the same tier wherever it is read.
 *
 * @param chain The chain key, or null.
 * @returns True on a testnet key, false on a mainnet key, or null when the key is
 *   not tier-bound.
 */
export function chainKeyTier(chain: string | null | undefined): boolean | null {
  const key = chain?.toLowerCase();
  if (!key) return null;
  if (TESTNET_CHAINS.has(key)) return true;
  if (MAINNET_CHAINS.has(key)) return false;
  return null;
}

/**
 * The human-readable network label for the nav indicator. Never the raw
 * network_env string, so a testnet deployment labeled "mainnet" still reads
 * Testnet.
 *
 * @param info Deployment signals from the config.
 * @returns "Testnet" or "Mainnet".
 */
export function networkLabel(info: NetworkInfo): 'Testnet' | 'Mainnet' {
  return effectiveEnvironment(info) === 'mainnet' ? 'Mainnet' : 'Testnet';
}

/**
 * Whether one wallet belongs to the deployment's active tier. A wallet is on the
 * active tier when its own testnet classification matches the effective
 * environment. This is the single predicate the tier filter and the Send
 * pre-submit guard share, so "on the active tier" means the same thing whether a
 * wallet is being kept in a list or checked at the submit boundary.
 *
 * @param wallet The wallet to check.
 * @param info Deployment signals plus the user's Developer Mode selection.
 * @returns True when the wallet is on the active tier.
 */
export function walletMatchesActiveTier(
  wallet: Pick<WalletSummary, 'chain' | 'curve'>,
  info: NetworkInfo,
): boolean {
  return walletIsTestnet(wallet, info) === (effectiveEnvironment(info) === 'testnet');
}

/**
 * The tier of one balance line, when the line names a concrete network. A unified
 * EVM wallet flattens every served network's holdings into one list spanning both
 * tiers, so the wallet-level gate alone cannot tell a Sepolia line from an
 * Ethereum line: both live under the same wallet. Each such line carries the
 * concrete network it came from, so its chain key decides its tier directly.
 *
 * Returns true for a line on a testnet network, false for a line on a mainnet
 * network, and null when the tier is unknown: either the line carries no network
 * (every non-EVM flat wallet line, whose whole wallet the wallet-level gate
 * already scopes) or the network's key is not one of the tier-bound EVM keys. A
 * null result means "defer to the wallet-level behavior", so an unclassified line
 * is never dropped by the per-line filter on its own.
 *
 * @param line The balance line to classify.
 * @returns True on testnet, false on mainnet, or null when the tier is unknown.
 */
export function lineIsTestnet(line: BalanceLine): boolean | null {
  const chain = line.network?.chainKey?.toLowerCase();
  if (!chain) return null;
  if (TESTNET_CHAINS.has(chain)) return true;
  if (MAINNET_CHAINS.has(chain)) return false;
  return null;
}

/**
 * Whether one balance line belongs to the active tier. A line whose tier is known
 * must match the effective environment. A line with an unknown tier (no per-line
 * network, or an unrecognized network key) is handled by tier, because the two
 * tiers do not carry the same risk.
 *
 * In testnet mode an unknown-tier line is kept: testnet is the fail-safe tier, a
 * testnet line shown in testnet mode is correct, and a mainnet line the gateway
 * failed to tag is already excluded because the deployment does not read mainnet
 * value into a testnet-mode session in practice.
 *
 * In mainnet mode the treatment of an unknown-tier line depends on the owning
 * wallet. A unified EVM wallet flattens every served EVM network into one list
 * that spans both tiers, so an EVM line the gateway failed to tag with a concrete
 * network could be a testnet line, and showing it as a mainnet amount would
 * mislabel testnet value as real funds. Such a line is treated as off-tier and
 * dropped, so the mainnet total never absorbs an untagged EVM line. A line from a
 * flat non-EVM wallet carries no per-line network by construction (its single
 * chain is on the enclosing view), and the wallet-level gate already scoped its
 * whole wallet to the active tier, so it is kept: dropping it would blank a
 * correctly scoped Solana, Sui, Bitcoin, or Tron line for no safety gain.
 *
 * The owning view's chain distinguishes the two unknown-tier cases. A unified EVM
 * view carries the chain key "evm"; a flat wallet carries its concrete chain. When
 * the caller does not supply the view chain, the safe reading is the flat-wallet
 * one (keep), preserving the prior behavior for callers that scope at the wallet
 * level only.
 *
 * This is the per-line counterpart to walletMatchesActiveTier, sharing the same
 * effective-environment rule so the two gates cannot disagree.
 *
 * @param line The balance line to check.
 * @param info Deployment signals plus the user's Developer Mode selection.
 * @param viewChain The owning wallet view's chain key, or undefined when unknown.
 * @returns True when the line is on the active tier or is a kept unknown-tier line.
 */
export function lineMatchesActiveTier(
  line: BalanceLine,
  info: NetworkInfo,
  viewChain?: string | null,
): boolean {
  const testnet = lineIsTestnet(line);
  const activeIsTestnet = effectiveEnvironment(info) === 'testnet';
  if (testnet !== null) return testnet === activeIsTestnet;

  // Unknown per-line tier. Testnet mode keeps it (the fail-safe tier). Mainnet
  // mode keeps it only when it comes from a flat non-EVM wallet the wallet-level
  // gate already placed on-tier; an untagged unified EVM line is dropped so a
  // testnet EVM line the gateway failed to tag cannot leak in as a mainnet amount.
  if (activeIsTestnet) return true;
  const isUnifiedEvm = (viewChain ?? '').toLowerCase() === 'evm';
  return !isUnifiedEvm;
}

/**
 * Narrow a wallet list to the tier the deployment is effectively on, keeping
 * only wallets whose own tier matches the active environment. The gateway
 * returns both tiers in one /v1/wallets response, so this client-side filter is
 * the single gate that keeps a mainnet wallet out of a testnet-mode session and
 * the reverse. Every surface that acts on a wallet (the home aggregate, the Send
 * source, the Receive target) narrows through this one helper so the tier rule
 * is defined in a single place and cannot drift between views.
 *
 * The classification per wallet fails safe to testnet on ambiguous config (see
 * walletIsTestnet), so a wallet whose tier cannot yet be determined is treated
 * as testnet and is included only while the session is on testnet.
 *
 * @param list The full wallet list, spanning both tiers.
 * @param info Deployment signals plus the user's Developer Mode selection.
 * @returns Only the wallets on the active tier.
 */
export function walletsForActiveTier(
  list: WalletSummary[],
  info: NetworkInfo,
): WalletSummary[] {
  return list.filter((wallet) => walletMatchesActiveTier(wallet, info));
}

/** A user-facing chain grouping, independent of the mainnet/testnet tier. */
export type ChainFamily = 'EVM' | 'Tron' | 'Solana' | 'Sui' | 'Bitcoin' | 'Other';

/** The chain families the dashboard filter offers, in display order. */
export const CHAIN_FAMILIES: readonly ChainFamily[] = [
  'EVM',
  'Tron',
  'Solana',
  'Sui',
  'Bitcoin',
];

/**
 * Group a wallet's chain key into a display family, collapsing mainnet and
 * testnet variants of the same chain together (for example both "tron" and
 * "tron-nile" are Tron). Unknown or null chains are "Other".
 *
 * @param chain The wallet's chain key.
 * @returns The chain family.
 */
export function chainFamily(chain: string | null): ChainFamily {
  const key = (chain ?? '').toLowerCase();
  if (!key) return 'Other';
  if (key === 'tron' || key === 'tron-nile') return 'Tron';
  // Accept the short 'sol' alias so an abbreviated key never falls to Other.
  if (key === 'solana' || key === 'solana-devnet' || key === 'sol') return 'Solana';
  if (key === 'sui' || key === 'sui-testnet') return 'Sui';
  // Accept the short 'btc' alias alongside every 'bitcoin' variant.
  if (key === 'btc' || key.startsWith('bitcoin')) return 'Bitcoin';
  // evm, ethereum, sepolia, base, base-sepolia, bsc, bsc-testnet, hyperevm,
  // hyperevm-testnet, and any other EVM-flavored key.
  if (
    key === 'evm' ||
    key.startsWith('eth') ||
    key.includes('sepolia') ||
    key === 'base' ||
    key.startsWith('bsc') ||
    key.startsWith('hyperevm')
  ) {
    return 'EVM';
  }
  return 'Other';
}
